import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type {
  TrainingAnswer,
  TrainingAnswers,
  TrainingAttemptSummary,
  TrainingModuleKind,
  TrainingPlayerPage,
  TrainingRetryMode,
  TrainingSection,
} from '@hcc/types';

/**
 * The player payload for one assigned training task. A quiz carries the
 * answer-free pages + grading fields; an external (redirect) module carries
 * only the link fields and none of the quiz fields, so the quiz-only members
 * below are absent when `kind === 'external'` — read `kind` and branch before
 * touching them (ModulePlayer returns the external screen early).
 */
export interface TrainingPlayerView {
  task_id: string;
  module_id: string;
  module_version_id: string;
  version: number;
  title: string;
  description: string | null;
  kind: TrainingModuleKind;
  /** external only. */
  external_url?: string | null;
  external_instructions?: string | null;
  external_confirmation?: string | null;
  /** False = an assessment: answers are recorded, never marked. */
  graded?: boolean;
  pass_mark: number;
  retry_mode: TrainingRetryMode;
  /** Pass-gated / grouping sections over the pages (empty when none). */
  sections: TrainingSection[];
  pages: TrainingPlayerPage[];
  status: 'pending' | 'awaiting_review' | 'changes_requested' | 'complete';
  attempts: TrainingAttemptSummary[];
  passed: boolean;
  best_score: number | null;
}

export interface TrainingDraftView {
  source: 'draft' | 'none';
  answers: TrainingAnswers;
  page_index: number;
  correct_question_ids: string[];
  watched_block_ids: string[];
  updated_at?: string | null;
}

/** Aggregate per-section outcome in a submit result (never per-question). */
export interface TrainingSubmitSection {
  section_id: string;
  title: string;
  score: number;
  passed: boolean;
  not_applicable: boolean;
}

export interface TrainingSubmitResult {
  /** False = an assessment: there is no score to report. */
  graded?: boolean;
  score: number;
  passed: boolean;
  pass_mark: number;
  attempt_number: number;
  sections: TrainingSubmitSection[];
}

const PLAYER_KEY = (taskId: string) => ['training-player', taskId] as const;
const DRAFT_KEY = (taskId: string) => ['training-draft', taskId] as const;

export function useTrainingPlayer(taskId: string | null) {
  return useQuery({
    queryKey: taskId ? PLAYER_KEY(taskId) : ['training-player', 'none'],
    enabled: !!taskId,
    queryFn: async () => api<TrainingPlayerView>(`/api/me/training/${taskId}/player`),
  });
}

/**
 * Resume state. Mirrors the form-draft query: never cache it — a stale
 * cached draft would clobber newer progress saved from another tab or
 * device, so every mount refetches.
 */
export function useTrainingDraft(taskId: string | null) {
  return useQuery({
    queryKey: taskId ? DRAFT_KEY(taskId) : ['training-draft', 'none'],
    enabled: !!taskId,
    gcTime: 0,
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: async () => api<TrainingDraftView>(`/api/me/training/${taskId}/draft`),
  });
}

export function useSaveTrainingDraft(taskId: string) {
  return useMutation({
    mutationFn: (input: {
      answers: TrainingAnswers;
      page_index: number;
      /** Ids of watch-gated video blocks completed so far. */
      watched_block_ids?: string[];
      /** The version the player displayed; pins the attempt on first save. */
      module_version_id?: string;
    }) =>
      api<{ ok: true }>(`/api/me/training/${taskId}/draft`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
  });
}

/** try_again mode: grade one question now; the response is a bare boolean. */
export function useCheckTrainingAnswer(taskId: string) {
  return useMutation({
    mutationFn: (input: {
      question_id: string;
      answer: TrainingAnswer;
      module_version_id?: string;
    }) =>
      api<{ correct: boolean }>(`/api/me/training/${taskId}/check`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
  });
}

export function useSubmitTraining(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { answers?: TrainingAnswers; module_version_id?: string }) =>
      api<TrainingSubmitResult>(`/api/me/training/${taskId}/submit`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me-view'] });
      qc.invalidateQueries({ queryKey: PLAYER_KEY(taskId) });
      qc.invalidateQueries({ queryKey: DRAFT_KEY(taskId) });
    },
  });
}

/**
 * External (redirect) modules only: the learner self-certifies completion.
 * No answers, no grade — just records the attestation, which completes the
 * task. Refreshes the /me view and player so the row flips to done.
 */
export function useCompleteExternalTraining(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ completed: true }>(`/api/me/training/${taskId}/complete`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me-view'] });
      qc.invalidateQueries({ queryKey: PLAYER_KEY(taskId) });
    },
  });
}
